import React from 'react';
import { CalendarClock, Plus, Trash2 } from 'lucide-react';

type TableItem = { id: string; label: string; capacity: number; active: boolean };

type TablesSettingsSectionProps = {
  tableModuleEnabled: boolean;
  tables: TableItem[];
  tableDraft: { label: string; capacity: string };
  tableSaving: boolean;
  setTables: React.Dispatch<React.SetStateAction<TableItem[]>>;
  setTableDraft: React.Dispatch<React.SetStateAction<{ label: string; capacity: string }>>;
  createTable: () => void | Promise<void>;
  updateTable: (tableId: string, updates: { label?: string; capacity?: number; active?: boolean }) => void | Promise<void>;
  deleteTable: (tableId: string) => void | Promise<void>;
};

const TablesSettingsSection: React.FC<TablesSettingsSectionProps> = ({
  tableModuleEnabled,
  tables,
  tableDraft,
  tableSaving,
  setTables,
  setTableDraft,
  createTable,
  updateTable,
  deleteTable,
}) => {
  if (!tableModuleEnabled) {
    return (
      <div id="secao-mesas" className="card w-full scroll-mt-24 space-y-4 p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15">
            <CalendarClock className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Mesas do salao</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              O modulo de mesas foi desabilitado pela plataforma para este restaurante.
            </p>
          </div>
        </div>
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
          O tenant continua operando normalmente, mas sem mapa de salao, ocupacao por mesa e configuracao de capacidade fisica.
        </div>
      </div>
    );
  }

  return (
    <div id="secao-mesas" className="card w-full scroll-mt-24 space-y-6 p-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15">
            <CalendarClock className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Mesas do salao</h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Cadastre as mesas fisicas do restaurante e quantas cadeiras cada uma comporta. O sistema usa isso para confirmar disponibilidade real.
            </p>
          </div>
        </div>
        <div className="grid gap-2 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-3 text-sm sm:grid-cols-3 xl:min-w-[420px]">
          <div>
            <p className="text-xs text-muted-foreground">Mesas</p>
            <p className="mt-1 text-xl font-black text-white">{tables.length}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Ativas</p>
            <p className="mt-1 text-xl font-black text-white">{tables.filter((table) => table.active).length}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Lugares</p>
            <p className="mt-1 text-xl font-black text-primary">
              {tables.filter((table) => table.active).reduce((total, table) => total + Number(table.capacity || 0), 0)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-5 md:grid-cols-[1fr,180px,auto]">
        <div className="space-y-2">
          <label className="text-sm font-medium text-white">Identificacao da mesa</label>
          <input
            value={tableDraft.label}
            onChange={(event) => setTableDraft((current) => ({ ...current, label: event.target.value }))}
            className="input"
            placeholder="Ex.: Mesa 01, Varanda 2, Janela A"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-white">Qtd. cadeiras</label>
          <input
            type="number"
            min="1"
            value={tableDraft.capacity}
            onChange={(event) => setTableDraft((current) => ({ ...current, capacity: event.target.value }))}
            className="input"
          />
        </div>
        <div className="flex items-end">
          <button type="button" onClick={createTable} disabled={tableSaving} className="btn-primary flex w-full items-center justify-center gap-2">
            <Plus className="h-4 w-4" />
            {tableSaving ? 'Salvando...' : 'Adicionar mesa'}
          </button>
        </div>
      </div>

      <div className="grid gap-3 2xl:grid-cols-2">
        {tables.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#2A2A2A] bg-[#121212] p-6 text-sm text-muted-foreground">
            Nenhuma mesa cadastrada ainda. Sem mesas ativas, o sistema nao consegue confirmar novas reservas.
          </div>
        ) : (
          tables.map((table) => (
            <div key={table.id} className="grid gap-4 rounded-2xl border border-[#2A2A2A] bg-[#121212] p-4 md:grid-cols-[1fr,160px,auto,auto] md:items-center">
              <input
                value={table.label}
                onChange={(event) => setTables((current) => current.map((item) => (item.id === table.id ? { ...item, label: event.target.value } : item)))}
                onBlur={(event) => updateTable(table.id, { label: event.target.value })}
                className="input"
              />
              <input
                type="number"
                min="1"
                value={table.capacity}
                onChange={(event) => setTables((current) => current.map((item) => (item.id === table.id ? { ...item, capacity: Number(event.target.value) } : item)))}
                onBlur={(event) => updateTable(table.id, { capacity: Number(event.target.value) })}
                className="input"
              />
              <button
                type="button"
                onClick={() => updateTable(table.id, { active: !table.active })}
                className={`rounded-xl border px-4 py-3 text-sm font-semibold ${
                  table.active ? 'border-primary bg-primary/15 text-white' : 'border-[#2A2A2A] bg-[#1A1A1A] text-muted-foreground'
                }`}
              >
                {table.active ? 'Mesa ativa' : 'Mesa pausada'}
              </button>
              <button type="button" onClick={() => deleteTable(table.id)} className="rounded-xl border border-[#2A2A2A] p-3 text-muted-foreground hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="rounded-2xl border border-[#2A2A2A] bg-[#1A1A1A]/60 p-4 text-sm text-muted-foreground">
        O sistema tenta encaixar cada reserva em uma mesa ativa com cadeiras suficientes no horario solicitado. Se nao houver mesa livre compativel, a reserva e bloqueada automaticamente.
      </div>
    </div>
  );
};

export default TablesSettingsSection;
